<template>
    <div class="main box">
        <h1>Roles</h1>
        <users-tabs></users-tabs>

        <p class="help">Tick the menus each role can open. Changes are saved immediately and apply on the user's next request.</p>

        <div class="table-container">
            <table>
                <thead>
                    <tr>
                        <th class="menu-col">Menu</th>
                        <th v-for="role in roles" :key="role.key">{{ role.name }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="permission in permissions" :key="permission.key">
                        <td class="menu-col">{{ permission.label }}</td>
                        <td v-for="role in roles" :key="role.key">
                            <input type="checkbox"
                                :checked="role.permissions.includes(permission.key)"
                                :disabled="role.isSystem || saving"
                                @change="toggle(role, permission.key, $event)" />
                        </td>
                    </tr>
                    <tr>
                        <td class="menu-col">Users &amp; roles</td>
                        <td v-for="role in roles" :key="role.key">
                            <input type="checkbox" :checked="role.key == 'admin'" disabled />
                        </td>
                    </tr>
                    <tr>
                        <td class="menu-col">Users with this role</td>
                        <td v-for="role in roles" :key="role.key">{{ role.userCount }}</td>
                    </tr>
                    <tr>
                        <td class="menu-col">Delete role</td>
                        <td v-for="role in roles" :key="role.key">
                            <div class="post-btn icon-otw_icon_trash" title="Delete this role"
                                v-if="!role.isSystem && role.userCount == 0" @click="confirmRole = role"></div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <form class="create-container" @submit.prevent="createRole">
            <div class="create-label">New role</div>
            <div class="create-row">
                <input type="text" placeholder="Role name*" maxlength="60" v-model.trim="newName" />
                <input type="submit" class="btn" :disabled="newName.length < 2" value="Create" />
            </div>
            <div class="notices errors" v-if="createError">{{ createError }}</div>
        </form>

        <div class="modal" v-if="confirmRole">
            <div class="modal-container">
                <h3>Please confirm deletion of this role</h3>
                <p class="confirm-name">{{ confirmRole.name }}</p>
                <div class="btns-container">
                    <div class="btn btn-cancel" @click="confirmRole = null">Cancel</div>
                    <div class="btn btn-confirm" @click="deleteRole">Confirm</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import UsersTabs from "./../components/UsersTabs.vue";

export default {
    name : "Roles",
    components : { UsersTabs },
    data() {
        return {
            roles : [],
            permissions : [],
            saving : false,
            newName : "",
            createError : null,
            confirmRole : null
        }
    },
    methods : {
        api(method, path, data) {
            return axios({
                method : method,
                url : `${this.$store.state.apiUrl}${path}`,
                data : data,
                validateStatus : () => true,
                headers : { 'x-access-token' : localStorage._safeToken }
            }).catch(() => ({ status : 0, data : { errors : "Unable to reach the API" } }));
        },
        errorText(res) {
            const errors = res.data && (res.data.errors || res.data.message);
            return Array.isArray(errors) ? errors.join(" ") : (typeof errors == "string" ? errors : "An error occurred.");
        },
        notify(msg, error) {
            this.$store.commit('setNotice', { msg : msg, error : error });
        },
        async load() {
            const res = await this.api("get", "/roles");
            if(res.status != 200) {
                this.notify(this.errorText(res), true);
                return;
            }
            this.roles = res.data.message;
            this.permissions = res.data.permissions;
        },
        async toggle(role, key, event) {
            const permissions = event.target.checked
                ? [...role.permissions, key]
                : role.permissions.filter(p => p != key);
            this.saving = true;
            const res = await this.api("put", `/roles/${role.key}`, { permissions : permissions });
            this.saving = false;
            if(res.status != 200) {
                event.target.checked = !event.target.checked;
                this.notify(this.errorText(res), true);
                return;
            }
            role.permissions = permissions;
            this.notify("Role updated.", false);
        },
        async createRole() {
            this.createError = null;
            const res = await this.api("post", "/roles", { name : this.newName, permissions : [] });
            if(res.status != 200) {
                this.createError = this.errorText(res);
                return;
            }
            this.newName = "";
            this.notify("Role created.", false);
            this.load();
        },
        async deleteRole() {
            const role = this.confirmRole;
            const res = await this.api("delete", `/roles/${role.key}`);
            this.confirmRole = null;
            if(res.status != 200) {
                this.notify(this.errorText(res), true);
                return;
            }
            this.notify("Role deleted.", false);
            this.load();
        }
    },
    mounted() {
        this.load();
    }
}
</script>

<style lang="scss" scoped>
    h1 {
        color:#fff;
        text-align: center;
        font-size:25px;
        text-transform: uppercase;
        font-weight: 300;
        margin:40px auto 20px auto;
    }

    .help {
        color:#fff;
        font-size:14px;
        text-align: center;
        font-weight: 300;
    }

    .menu-col {
        text-align: left;
        width:260px;
    }

    td input[type="checkbox"] {
        margin:0;
    }

    td input[type="checkbox"]:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    .create-container {
        margin: 40px auto;
        padding: 20px;
        border: 1px solid #333;
    }

    .create-label {
        color:#fff;
        font-size:14px;
        margin-bottom:15px;
    }

    .create-row {
        display: flex;
        align-items: center;
        gap:15px;
    }

    .create-row input[type="text"] {
        flex:1;
    }

    .create-row .btn {
        padding:11px 25px;
        font-size:15px;
        text-transform: uppercase;
    }

    .notices.errors {
        margin-top:15px;
    }

    .confirm-name {
        color:#fff;
        margin-top:10px;
    }

    .modal {
        position:fixed;
        top:0;
        bottom:0;
        left:0;
        right:0;
        z-index:1;
        background:rgba(255,255,255, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .modal-container {
        padding:40px;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
        background:#000;
        width:500px;
    }

    .modal-container h3 {
        font-size:20px;
        color:#fff;
        text-align: center;
        font-weight: 300;
    }

    .btns-container {
        display: flex;
        margin-top:20px;
    }

    .btns-container .btn {
        margin:0 20px;
    }

    .btn-cancel {
        background:#000;
        border:1px solid #fff;
    }

    .btn-cancel:hover {
        background:#fff;
        color:#000;
    }

    /* delete icons centered under their column */
    td .post-btn {
        margin:0 auto;
    }

    @media (max-width: 1024px) {
        table {
            min-width:640px;
        }

        .menu-col {
            width:auto;
            min-width:150px;
        }
    }

    @media (max-width: 700px) {
        .create-row {
            flex-direction: column;
            align-items: stretch;
        }

        .create-container {
            padding:15px;
        }
    }
</style>
